import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';

import { font_sizes } from '@hookdeck/theme';

import useComponentDefaultProps from '../../../../hooks/useComponentDefaultProps';
import Text from '../../../base/Text';
import { Div, StyledUtilsProps } from '../../../helpers/StyledUtils';
import InputError from './InputError';
import InputHelp from './InputHelp';

export type Variant = 'card';

export interface InputBaseProps {
  name: string;
  label?: string;
  label_size?: keyof typeof font_sizes;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  help?: React.ReactNode;
  error?: React.ReactNode;
  with_asterisk?: boolean;
  minimal?: boolean;
  inputContainer?(children: React.ReactNode): React.ReactNode;
}

export interface InputProps
  extends StyledUtilsProps, Omit<InputBaseProps, 'name'>, React.ComponentPropsWithoutRef<'div'> {
  children: React.ReactNode;
  id?: string;
}

const defaultProps: Partial<InputProps> = {
  inputContainer: (children) => children,
  with_asterisk: false,
  minimal: false,
};

const StyledInput = styled(Div)``;
const StyledFieldWrapper = styled(Div)<{ error: boolean }>(
  ({ theme, error }) => css`
    box-sizing: border-box;
    -moz-box-sizing: border-box;
    -webkit-box-sizing: border-box;

    ${StyledInput} {
      flex-grow: 1;

      input,
      select {
        box-sizing: border-box;
        width: 100%;
        font-size: ${theme.pxToRem(theme.font_sizes.m.font_size)};
        line-height: ${theme.pxToRem(theme.font_sizes.m.line_height)};
        padding: ${theme.spacing(1.5)} ${theme.spacing(3)};
        outline: none;
        border: ${theme.border};
        border-radius: ${theme.radius.normal};

        &:focus {
          box-shadow: ${theme.shadows.input.focused};
        }

        ${error &&
        css`
          border-color: ${theme.colors.outline.danger};
        `}
      }
    }
  `,
);

export const Input = forwardRef<HTMLDivElement, InputProps>((props, ref) => {
  const { label, children, required, id, error, help, inputContainer, label_size, ...others } =
    useComponentDefaultProps(defaultProps, props);

  return (
    <Div flex={{ direction: 'column', gap: 1 }} ref={ref} {...others}>
      <StyledFieldWrapper error={!!error}>
        {label && (
          <Text
            muted
            semi
            size={label_size || 'xs'}
            as="label"
            key="label"
            id={id ? `${id}-label` : undefined}
            htmlFor={id}>
            {label}
            {required && (
              <Text danger as="span">
                *
              </Text>
            )}
          </Text>
        )}
        <StyledInput>{inputContainer!(children)}</StyledInput>
      </StyledFieldWrapper>
      {help && <InputHelp key="help">{help}</InputHelp>}
      {error && <InputError key="error">{error}</InputError>}
    </Div>
  );
});

export default Input;
